import React, { createContext, useState, useEffect, useCallback } from "react";

export const CartContext = createContext();

const getInitialCart = () => {
  try {
    const savedCart = localStorage.getItem("shahrizada-cart");
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error("Failed to parse cart from localStorage", error);
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(getInitialCart);

  useEffect(() => {
    localStorage.setItem("shahrizada-cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product, variant, quantity = 1) => {
    setCart((prevCart) => {
      const existingItemIndex = prevCart.findIndex(
        (item) => item.variant_id === variant.variant_id
      );

      if (existingItemIndex > -1) {
        const newCart = [...prevCart];
        const existingItem = newCart[existingItemIndex];
        existingItem.quantity += quantity;
        existingItem.totalPrice = existingItem.quantity * existingItem.price;
        return newCart;
      } else {
        const productData = {
          product_id: product.product_id,
          product_name: product.product_name,
          composition: product.composition,
          image_url: product.image_url,
        };
        return [
          ...prevCart,
          {
            ...productData,
            ...variant,
            quantity: quantity,
            totalPrice: quantity * variant.price,
          },
        ];
      }
    });
  }, []);

  const removeFromCart = useCallback((variantId) => {
    setCart((prevCart) =>
      prevCart.filter((item) => item.variant_id !== variantId)
    );
  }, []);

  const updateQuantity = useCallback((variantId, newQuantity) => {
    setCart((prevCart) => {
      if (newQuantity < 1) {
        return prevCart.filter((item) => item.variant_id !== variantId);
      }
      return prevCart.map((item) =>
        item.variant_id === variantId
          ? {
              ...item,
              quantity: newQuantity,
              totalPrice: newQuantity * item.price,
            }
          : item
      );
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce((sum, item) => sum + item.totalPrice, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
